import { MeetingDayDto, MeetingEventTypeDto, MeetingStatusDto } from '@/api/generated';
import { MeetingsSearchFormModel } from '@/views/meetings/model/MeetingsSearchForm.model.ts';

export type MeetingsViewTranslation = {
  body: {
    details: string;
    moreParticipants: string;
    noteSent: string;
    sendToErecruiter: string;
  };
  columns: {
    actions: string;
    participants: string;
    recruitment: string;
    startAt: string;
    status: string;
    subject: string;
  };
  days: {
    disabledHint: string;
    options: Record<MeetingDayDto, string>;
  };
  details: {
    activity: string;
    addFeedback: string;
    aiVersion: string;
    aiVersionHint: string;
    automatically: string;
    cancelEdit: string;
    currentVersion: string;
    downloadDocument: string;
    downloadPdf: string;
    duration: string;
    editedAfterSend: string;
    editHint: string;
    editNote: string;
    editPlaceholder: string;
    events: Record<MeetingEventTypeDto, string>;
    feedback: string;
    feedbackPlaceholder: string;
    hideSent: string;
    joinMeeting: string;
    language: string;
    lastEdited: string;
    metrics: {
      botLeaveDelay: string;
      noteDelay: string;
      recording: string;
      recordingHint: string;
      transcriptDelay: string;
    };
    noActivity: string;
    noFeedback: string;
    noNote: string;
    note: string;
    noteSendFailed: string;
    noteSent: string;
    noTranscript: string;
    participants: string;
    parts: {
      charsOfLimit: string;
      charsTotal: string;
      draftChars: string;
      draftCharsOfLimit: string;
      fitsInOne: string;
      orderHint: string;
      partChars: string;
      partLabel: string;
      splitInto: string;
      title: string;
    };
    recruitment: string;
    restoreAiVersion: string;
    saveNote: string;
    sentInParts: string;
    showSent: string;
    startAt: string;
    transcript: string;
    unknownAuthor: string;
  };
  filters: Record<keyof MeetingsSearchFormModel, string>;
  statuses: Record<MeetingStatusDto, string>;
};

export const meetingsViewTranslation: MeetingsViewTranslation = {
  body: {
    details: 'Details',
    moreParticipants: 'and {{count}} more',
    noteSent: 'Sent',
    sendToErecruiter: 'Send to eRecruiter',
  },
  columns: {
    actions: 'Actions',
    participants: 'Participants',
    recruitment: 'Recruitment',
    startAt: 'Start',
    status: 'Status',
    subject: 'Subject',
  },
  days: {
    disabledHint: 'Inactive while a date range is set — clear it to use the quick filters again.',
    options: {
      [MeetingDayDto.Today]: 'Today',
      [MeetingDayDto.Tomorrow]: 'Tomorrow',
      [MeetingDayDto.Yesterday]: 'Yesterday',
    },
  },
  details: {
    activity: 'Bot and system activity',
    addFeedback: 'Add feedback',
    aiVersion: 'AI version',
    aiVersionHint:
      'The version the model produced, kept as it was written. It has been edited since, so it cannot be sent — switch back to the current one.',
    automatically: 'automatically',
    cancelEdit: 'Cancel',
    currentVersion: 'Current',
    downloadDocument: 'Download document (PDF)',
    downloadPdf: 'Download PDF',
    duration: 'Duration',
    editedAfterSend:
      'The note was edited after it had been sent. Send it again to pass the current version to eRecruiter — it will be added as a new entry.',
    editHint:
      'Formatting: ## section heading, ### subheading, - bullet, **bold**. The same markup is used for the PDF and for eRecruiter.',
    editNote: 'Edit',
    editPlaceholder: 'Note content (Markdown)',
    events: {
      [MeetingEventTypeDto.BotInCall]: 'Bot in the call',
      [MeetingEventTypeDto.BotJoining]: 'Bot joining the meeting',
      [MeetingEventTypeDto.BotLeft]: 'Bot left the meeting',
      [MeetingEventTypeDto.BotRecording]: 'Recording started',
      [MeetingEventTypeDto.EveryoneLeft]: 'Everyone left the meeting',
      [MeetingEventTypeDto.FeedbackAdded]: 'Feedback added',
      [MeetingEventTypeDto.FeedbackEdited]: 'Feedback edited',
      [MeetingEventTypeDto.MeetingFailed]: 'Meeting failed',
      [MeetingEventTypeDto.NoteDocSent]: 'Note sent to eRecruiter as .docx (no longer used)',
      [MeetingEventTypeDto.NoteEdited]: 'Note edited',
      [MeetingEventTypeDto.NoteReady]: 'Note generated',
      [MeetingEventTypeDto.NoteSendFailed]: 'Sending the note failed',
      [MeetingEventTypeDto.NoteSent]: 'Note sent to eRecruiter',
      [MeetingEventTypeDto.RecordingReady]: 'Recording ready',
      [MeetingEventTypeDto.Transcribing]: 'Transcribing',
      [MeetingEventTypeDto.TranscriptFailed]: 'Transcription failed',
      [MeetingEventTypeDto.TranscriptReady]: 'Transcript ready',
    },
    feedback: 'Recruiter feedback',
    feedbackPlaceholder: 'Your feedback after the interview',
    hideSent: 'hide what was sent',
    joinMeeting: 'Join meeting',
    language: 'Interview language',
    lastEdited: 'Edited',
    metrics: {
      botLeaveDelay: 'Everyone left → bot left',
      noteDelay: 'Everyone left → note',
      recording: 'Recording time',
      recordingHint: 'what Recall bills for',
      transcriptDelay: 'Everyone left → transcript',
    },
    noActivity: 'No activity recorded yet',
    noFeedback: 'No feedback yet',
    noNote: 'No note yet',
    note: 'Note',
    noteSendFailed: 'Sending to eRecruiter failed',
    noteSent: 'Sent to eRecruiter',
    noTranscript: 'No transcript yet',
    participants: 'Participants',
    parts: {
      charsOfLimit: '{{total}} of {{max}} characters',
      charsTotal: '{{total}} characters in total, {{max}} per part',
      draftChars: '{{chars}} characters',
      draftCharsOfLimit: '{{chars}} / {{max}} characters per part',
      fitsInOne: 'Fits into a single eRecruiter note',
      orderHint:
        'Parts are sent last one first, so they read top down in eRecruiter. Each part carries its [1/n] marker, counted into the limit.',
      partChars: '{{chars}} / {{max}} characters',
      partLabel: 'Part {{number}} of {{count}}',
      splitInto: 'Will be sent in {{count}} parts',
      title: 'What goes to eRecruiter',
    },
    recruitment: 'Recruitment',
    restoreAiVersion: 'Restore AI version',
    saveNote: 'Save',
    sentInParts: 'sent in {{count}} parts',
    showSent: 'show what was sent',
    startAt: 'Start',
    transcript: 'Transcript',
    unknownAuthor: 'Unknown author',
  },
  filters: {
    dateFrom: 'Date from',
    dateTo: 'Date to',
    days: 'Quick filters',
    organizers: 'Meeting organizer',
    searchPhrase: 'Subject or participant...',
    statuses: 'Statuses',
  },
  statuses: {
    [MeetingStatusDto.Cancelled]: 'Cancelled',
    [MeetingStatusDto.Discovered]: 'Discovered',
    [MeetingStatusDto.Failed]: 'Failed',
    [MeetingStatusDto.InCall]: 'In call',
    [MeetingStatusDto.Joining]: 'Joining',
    [MeetingStatusDto.NoteReady]: 'Note ready',
    [MeetingStatusDto.Recording]: 'Recording',
    [MeetingStatusDto.RecordingDone]: 'Recording done',
    [MeetingStatusDto.Scheduled]: 'Scheduled',
    [MeetingStatusDto.Transcribing]: 'Transcribing',
    [MeetingStatusDto.TranscriptReady]: 'Transcript ready',
  },
};
